import i18n from "./i18n";

/**
 * 日本語名/英語名を持つエンティティから、現在のUI表示言語に応じた名称を取得する共通ヘルパー。
 * - UI表示言語が英語（'en'）かつ英語名フィールドに値がある場合のみ英語名を返す
 * - それ以外（日本語、または英語名が未設定）の場合は日本語名を返す
 *
 * @param entity 日本語名/英語名を持つオブジェクト
 * @param jaKey 日本語名のプロパティキー
 * @param enKey 英語名のプロパティキー
 * @returns 表示言語に応じた名称（該当するキーの値がない場合は空文字）
 */
export const getBilingualDisplayName = <T extends object>(
  entity: T | null | undefined,
  jaKey: keyof T & string,
  enKey: keyof T & string,
): string => {
  if (!entity) return "";
  const record = entity as Record<string, unknown>;
  const enValue = record[enKey];
  const jaValue = record[jaKey];
  if (i18n.language === "en" && typeof enValue === "string" && enValue) {
    return enValue;
  }
  return typeof jaValue === "string" ? jaValue : "";
};

/**
 * 言語コードをキーに持つオブジェクト（例: { ja: "...", en: "..." }）から、
 * 現在のUI表示言語に応じた名称を取得する共通ヘルパー。
 * - `entity[i18n.language]` に値があればそれを返す
 * - なければ `fallbackKey`（デフォルト 'ja'）の値を返す
 * - 将来言語を追加してもこの関数自体の改修は不要（オブジェクトにキーを追加するだけで対応可能）
 *
 * @param entity 言語コードをキーに持つオブジェクト
 * @param fallbackKey 表示言語に該当するキーが無い場合のフォールバックキー
 * @returns 表示言語に応じた名称（該当するキーの値がない場合は空文字）
 */
export const getMultilingualDisplayName = <T extends object>(
  entity: T | null | undefined,
  fallbackKey: keyof T & string = "ja" as keyof T & string,
): string => {
  if (!entity) return "";
  const record = entity as Record<string, unknown>;
  const currentValue = record[i18n.language];
  if (typeof currentValue === "string" && currentValue) {
    return currentValue;
  }
  const fallbackValue = record[fallbackKey];
  return typeof fallbackValue === "string" ? fallbackValue : "";
};

// ---------------------------------------------------------------------------------------------------------------
/**
 * divisionHierarchy(日本語)/divisionHierarchyEn(英語) を持つエンティティから、
 * 現在の表示言語に応じた事業部階層文字列（"本部 / 事業部 / ..."）を取得する。
 * getBilingualDisplayName と同様、英語名が無ければ日本語名にフォールバックする。
 */
export const getDivisionHierarchyDisplay = <
  T extends { divisionHierarchy?: string | null; divisionHierarchyEn?: string | null }
>(item: T | null | undefined): string => {
  if (!item) return "";
  if (i18n.language === "en" && item.divisionHierarchyEn) {
    return item.divisionHierarchyEn;
  }
  return item.divisionHierarchy ?? "";
};

/**
 * 事業部階層文字列（"本部 / 事業部 / 部 / ..."）から、指定インデックスの要素を取り出す。
 * index=0: 本部名、index=2: 事業部名 など。
 * 区切り文字前後の空白差異（保存時は `" / "` 結合だが `split("/")` 直後は要素にスペースが残る）を
 * 吸収するため、分割後に各要素を trim してから返す。
 * ※ 本関数は外部には公開せず（export しない）、`i18nDisplay.ts` 内部の実装専用ヘルパーとする。
 *   呼び出し側には必ず後述の意味のある名前の関数（`getHeadquartersNameFromHierarchy` /
 *   `getDivisionNameFromHierarchy`）経由で使わせ、階層インデックスの知識を呼び出し側に持たせない。
 */
const getDivisionHierarchyPartByIndex = <
  T extends { divisionHierarchy?: string | null; divisionHierarchyEn?: string | null }
>(item: T | null | undefined, index: number): string => {
  const parts = getDivisionHierarchyDisplay(item).split("/").map((p) => p.trim());
  return parts[index] ?? "";
};

/**
 * 事業部階層文字列から本部名（先頭要素）のみを取り出す。
 * 既存の `item.divisionHierarchy.split("/")[0]` はすべてこの関数に置き換える。
 */
export const getHeadquartersNameFromHierarchy = <
  T extends { divisionHierarchy?: string | null; divisionHierarchyEn?: string | null }
>(item: T | null | undefined): string => {
  return getDivisionHierarchyPartByIndex(item, 0);
};

/**
 * 事業部階層文字列から事業部名（3番目の要素、index=2）のみを取り出す。
 * 既存の `item.divisionHierarchy.split(" / ")[2]` はすべてこの関数に置き換える。
 * （一覧画面の「事業部」列表示で多用されているパターン）
 *
 * 階層が3階層に満たない（本部/事業部の2階層のみ等）データの場合、index=2 の要素が存在しないため
 * index=1（2番目の要素）にフォールバックする。この仕様はもともと ReviewSearchTab.tsx のみが
 * 個別に実装していたが、全画面で表示・ソート・フィルタの挙動を一致させるため共通関数側に統合した
 * （実装・動作確認の結果、呼び出し側ごとにフォールバック有無が異なると同一データで列表示・
 * 検索結果が食い違う不整合が起きるため、共通関数に組み込む方針を採用）。
 */
export const getDivisionNameFromHierarchy = <
  T extends { divisionHierarchy?: string | null; divisionHierarchyEn?: string | null }
>(item: T | null | undefined): string => {
  const primary = getDivisionHierarchyPartByIndex(item, 2);
  if (primary) return primary;
  return getDivisionHierarchyPartByIndex(item, 1);
};